import { createBrowserRouter, Navigate } from 'react-router-dom'
import { MainLayout, AdminLayout } from '@/design-system/layouts'
import { PrivateRoute, RoleBasedRoute } from '@/features/auth/components'
import { LoginPage, ChangePasswordPage, ForgotPasswordPage, ResetPasswordPage } from '@/features/auth/pages'
import { AdminLandingPage } from '@/features/administration/pages'
import { ActionsListPage, ActionFormPage } from '@/features/actions/pages'
import { TypesActiviteListPage, TypeActiviteFormPage } from '@/features/types-activite/pages'
import { MatieresListPage, MatiereFormPage } from '@/features/matieres/pages'
import { PeriodesListPage, PeriodeFormPage } from '@/features/periodes/pages'
import { ProgrammesListPage, ProgrammeFormPage } from '@/features/programmes/pages'
import { StructuresListPage, StructureFormPage } from '@/features/structures/pages'
import { EtudiantsListPage, EtudiantDetailPage } from '@/features/etudiants/pages'
import { DashboardMobilePage, ScanMobilePage, ActionTimerMobilePage, CalendarMobilePage, ProfileMobilePage, EventsMobilePage, EventCreateMobilePage } from '@/features/mobile/pages'
import { ProfileDesktopPage } from '@/features/profile/pages/profile-desktop-page'
import { DashboardDesktopPage } from '@/features/dashboard/pages'
import { AdminDashboardPage, FormateursListPage, FormateurCreatePage, FormateurDetailPage, AdminLogsPage, AdminErrorsPage, AdminSystemPage } from '@/features/admin/pages'
import { useAuth } from '@/features/auth/hooks/use-auth'

// Placeholder component for pages not yet implemented
function PlaceholderPage({ title }: { title: string }) {
  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-bold text-neutral-900">{title}</h1>
      <p className="text-neutral-500">Cette page sera implémentée dans une prochaine phase.</p>
    </div>
  )
}

// Layout switch: admin gets AdminLayout, others get MainLayout
function LayoutSwitch() {
  const { user } = useAuth()
  if (user?.role === 'admin') return <AdminLayout />
  return <MainLayout />
}

// Responsive dashboard: admin dashboard, mobile for students, desktop otherwise
function ResponsiveDashboard() {
  const { user } = useAuth()
  if (user?.role === 'admin') {
    return <AdminDashboardPage />
  }
  const isMobile = window.innerWidth < 768
  if (user?.role === 'etudiant' && isMobile) {
    return <DashboardMobilePage />
  }
  return <DashboardDesktopPage />
}

// Scan page: always mobile-style (fullscreen, no sidebar)
function ResponsiveScan() {
  return <ScanMobilePage />
}

// Profile: mobile view for students, desktop page otherwise
function ResponsiveProfile() {
  const { user } = useAuth()
  const isMobile = window.innerWidth < 768
  if (user?.role === 'etudiant' && isMobile) {
    return <ProfileMobilePage />
  }
  return <ProfileDesktopPage />
}

// Events: mobile view for students, desktop placeholder otherwise
function ResponsiveEvents() {
  const { user } = useAuth()
  const isMobile = window.innerWidth < 768
  if (user?.role === 'etudiant' && isMobile) {
    return <EventsMobilePage />
  }
  return <PlaceholderPage title="Événements" />
}

// Event creation: mobile view for students
function ResponsiveEventCreate() {
  const { user } = useAuth()
  const isMobile = window.innerWidth < 768
  if (user?.role === 'etudiant' && isMobile) {
    return <EventCreateMobilePage />
  }
  return <PlaceholderPage title="Nouvel événement" />
}

// Calendar: mobile view for students, desktop placeholder otherwise
function ResponsiveCalendar() {
  const { user } = useAuth()
  const isMobile = window.innerWidth < 768
  if (user?.role === 'etudiant' && isMobile) {
    return <CalendarMobilePage />
  }
  return <PlaceholderPage title="Calendrier" />
}

export const router = createBrowserRouter([
  // Public routes
  { path: '/login', element: <LoginPage /> },
  { path: '/forgot-password', element: <ForgotPasswordPage /> },
  { path: '/reset-password', element: <ResetPasswordPage /> },

  // Protected routes — Fullscreen (no MainLayout)
  {
    element: <PrivateRoute />,
    children: [
      { path: '/change-password', element: <ChangePasswordPage /> },
      { path: '/scan', element: <ResponsiveScan /> },
      { path: '/action-timer', element: <ActionTimerMobilePage /> },
    ],
  },

  // Protected routes — With role-based layout (Admin or Main)
  {
    element: <PrivateRoute />,
    children: [
      {
        element: <LayoutSwitch />,
        children: [
          {
            element: <RoleBasedRoute />,
            children: [
              { path: '/dashboard', element: <ResponsiveDashboard /> },
              { path: '/etudiants', element: <EtudiantsListPage /> },
              { path: '/etudiants/create', element: <EtudiantDetailPage /> },
              { path: '/etudiants/:id', element: <EtudiantDetailPage /> },

              // Programmes (shared between admin and formateur)
              { path: '/programmes', element: <ProgrammesListPage /> },
              { path: '/programmes/create', element: <ProgrammeFormPage /> },
              { path: '/programmes/:id', element: <ProgrammeFormPage /> },

              // Administration / Référentiels (shared)
              { path: '/administration', element: <AdminLandingPage /> },
              { path: '/administration/actions', element: <ActionsListPage /> },
              { path: '/administration/actions/create', element: <ActionFormPage /> },
              { path: '/administration/actions/:id', element: <ActionFormPage /> },
              { path: '/administration/matieres', element: <MatieresListPage /> },
              { path: '/administration/matieres/create', element: <MatiereFormPage /> },
              { path: '/administration/matieres/:id', element: <MatiereFormPage /> },
              { path: '/administration/types-activite', element: <TypesActiviteListPage /> },
              { path: '/administration/types-activite/create', element: <TypeActiviteFormPage /> },
              { path: '/administration/types-activite/:id', element: <TypeActiviteFormPage /> },
              { path: '/administration/periodes', element: <PeriodesListPage /> },
              { path: '/administration/periodes/create', element: <PeriodeFormPage /> },
              { path: '/administration/periodes/:id', element: <PeriodeFormPage /> },
              { path: '/administration/structures', element: <StructuresListPage /> },
              { path: '/administration/structures/create', element: <StructureFormPage /> },
              { path: '/administration/structures/:id', element: <StructureFormPage /> },

              // Admin-only pages
              { path: '/formateurs', element: <FormateursListPage /> },
              { path: '/formateurs/create', element: <FormateurCreatePage /> },
              { path: '/formateurs/:id', element: <FormateurDetailPage /> },
              { path: '/logs', element: <AdminLogsPage /> },
              { path: '/erreurs', element: <AdminErrorsPage /> },
              { path: '/systeme', element: <AdminSystemPage /> },

              // Other pages
              { path: '/profil', element: <ResponsiveProfile /> },
              { path: '/planning', element: <PlaceholderPage title="Planning" /> },
              { path: '/calendar', element: <ResponsiveCalendar /> },
              { path: '/events', element: <ResponsiveEvents /> },
              { path: '/events/create', element: <ResponsiveEventCreate /> },
              { path: '/events/:id/edit', element: <ResponsiveEventCreate /> },
            ],
          },
        ],
      },
    ],
  },

  // Default redirect
  { path: '/', element: <Navigate to="/dashboard" replace /> },
  { path: '*', element: <Navigate to="/dashboard" replace /> },
])
